import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, XAxis, YAxis } from 'recharts';

import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { UiBlock } from '../../../../common/hooks/use-assistant-api.hook';
import { chartRows, compact } from './blocks';

type ChartBlock = Extract<UiBlock, { type: 'chart' }>;

/**
 * A chart the assistant drew from figures it read.
 *
 * Monochrome, like the rest of the app: ink for the first series, then lighter
 * greys. Colour is kept for meaning, and a series is not a meaning.
 */
const INKS = [
    'var(--foreground)',
    'color-mix(in oklch, var(--foreground) 55%, transparent)',
    'color-mix(in oklch, var(--foreground) 32%, transparent)',
    'color-mix(in oklch, var(--foreground) 18%, transparent)',
    'color-mix(in oklch, var(--foreground) 10%, transparent)',
];

export const ReplyChart: React.FC<{ block: ChartBlock }> = ({ block }) => {
    const { t } = useTranslation();
    const title = block.titleKey ? (t(`AGENTS.UI.TITLE.${block.titleKey}`, { defaultValue: block.title }) as string) : block.title;
    const rows = chartRows(block.series);
    const config: ChartConfig = Object.fromEntries(
        block.series.map((s, i) => [`s${i}`, { label: s.name, color: INKS[i % INKS.length] }]),
    );
    const format = (v: number) => compact(Number(v), block.unit);

    let chart: React.ReactElement;

    if (block.kind === 'hbar') {
        // A ranking: biggest first, labels on the left, as tall as it needs.
        const ranked = [...rows].sort((a, b) => (b.s0 ?? 0) - (a.s0 ?? 0));
        chart = (
            <ChartContainer config={config} className="aspect-auto w-full" style={{ height: Math.max(120, ranked.length * 30 + 20) }}>
                <BarChart data={ranked} layout="vertical" margin={{ left: 4, right: 36 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="x" width={110} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideIndicator />} />
                    <Bar
                        dataKey="s0"
                        fill="var(--color-s0)"
                        radius={4}
                        isAnimationActive={false}
                        label={{ position: 'right', fontSize: 11, formatter: (v: any) => format(v) }}
                    />
                </BarChart>
            </ChartContainer>
        );
    } else if (block.kind === 'donut') {
        const slices = rows.map((r) => ({ name: r.x, value: r.s0 ?? 0 }));
        const total = slices.reduce((n, s) => n + s.value, 0) || 1;
        chart = (
            <div className="flex items-center gap-3">
                <ChartContainer config={config} className="aspect-square h-[140px] shrink-0">
                    <PieChart>
                        <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                        <Pie data={slices} dataKey="value" nameKey="name" innerRadius={38} outerRadius={64} strokeWidth={2} isAnimationActive={false}>
                            {slices.map((_, i) => <Cell key={i} fill={INKS[i % INKS.length]} />)}
                        </Pie>
                    </PieChart>
                </ChartContainer>
                <ul className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                    {slices.map((s, i) => (
                        <li key={s.name} className="flex items-center gap-2">
                            <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: INKS[i % INKS.length] }} />
                            <span className="truncate">{s.name}</span>
                            <span className="ml-auto tabular-nums text-muted-foreground">{Math.round((s.value / total) * 100)}%</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    } else if (block.kind === 'bar') {
        chart = (
            <ChartContainer config={config} className="aspect-auto h-[180px] w-full">
                <BarChart data={rows} margin={{ left: -12, right: 4 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="x" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                    <YAxis tickLine={false} axisLine={false} width={44} tickFormatter={format} tick={{ fontSize: 11 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {block.series.map((_, i) => (
                        <Bar key={i} dataKey={`s${i}`} fill={`var(--color-s${i})`} radius={3} isAnimationActive={false} />
                    ))}
                </BarChart>
            </ChartContainer>
        );
    } else {
        chart = (
            <ChartContainer config={config} className="aspect-auto h-[180px] w-full">
                <LineChart data={rows} margin={{ left: -12, right: 8 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="x" tickLine={false} axisLine={false} minTickGap={24} tick={{ fontSize: 11 }} />
                    <YAxis tickLine={false} axisLine={false} width={44} tickFormatter={format} tick={{ fontSize: 11 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {block.series.map((_, i) => (
                        <Line key={i} type="monotone" dataKey={`s${i}`} stroke={`var(--color-s${i})`} strokeWidth={1.75} dot={false} isAnimationActive={false} />
                    ))}
                </LineChart>
            </ChartContainer>
        );
    }

    return (
        <div className="mt-2.5 rounded-xl border p-3">
            {title && <p className="mb-2 text-xs font-medium text-muted-foreground">{title}</p>}
            {chart}
            {block.series.length > 1 && block.kind !== 'donut' && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {block.series.map((s, i) => (
                        <span key={s.name} className="inline-flex items-center gap-1.5">
                            <span className="size-2.5 rounded-sm" style={{ backgroundColor: INKS[i % INKS.length] }} />
                            {s.name}
                        </span>
                    ))}
                </div>
            )}
            {block.note && <p className="mt-2 text-xs text-muted-foreground">{block.note}</p>}
        </div>
    );
};
